import { FormControl, FormField as ShadcnFormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useTranslation } from "react-i18next"
import { ControllerRenderProps, Path, type FieldValues, type UseFormReturn } from "react-hook-form"
import { ReactNode } from "react"

interface MezzaFormFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>
  name: keyof T
  /** i18n key passed to `t()`. Ignored when `labelText` is set. */
  label?: string
  /** Pre-translated label (e.g. dynamic copy with currency code). */
  labelText?: string
  placeholder?: string
  type?: "text" | "email" | "number" | "textarea" | "password"
  inputMode?: "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url" | "none"
  className?: string
  prefix?: string
  suffix?: ReactNode
  disabled?: boolean
  autoFocus?: boolean
  onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onMouseDown?: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

export function MezzaFormField<T extends FieldValues>({
  form,
  name,
  label,
  labelText,
  placeholder,
  type = "text",
  inputMode,
  className,
  prefix,
  suffix,
  disabled,
  autoFocus,
  onFocus,
  onMouseDown,
}: MezzaFormFieldProps<T>) {
  const { t } = useTranslation()

  const handleChange = (field: ControllerRenderProps<T>, e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === "number") {
      const value = e.target.value;
      field.onChange(value === "" ? "" : Number(value));
    } else {
      field.onChange(e);
    }
  }
  
  return (
    <ShadcnFormField
      control={form.control}
      name={name as Path<T>}
      render={({ field }) => (
        <FormItem>
          {labelText ? <FormLabel>{labelText}</FormLabel> : label ? <FormLabel>{t(label)}</FormLabel> : null}
          <FormControl>
            {type === "textarea" ? (
              <Textarea
                onFocus={onFocus}
                onMouseDown={onMouseDown}
                placeholder={placeholder ? t(placeholder) : undefined}
                className={className}
                {...field}
                disabled={disabled}
              />
            ) : (
              <Input
                autoFocus={autoFocus}
                onFocus={onFocus}
                onMouseDown={onMouseDown}
                type={type}
                inputMode={inputMode}
                placeholder={placeholder ? t(placeholder) : undefined}
                className={className}
                {...field}
                onBlur={field.onBlur}
                onChange={(e) => handleChange(field, e)}
                prefix={prefix}
                suffix={suffix}
                disabled={disabled}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
} 